/**
 * Payment type select panel
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import HttpStatus from 'http-status-codes';
import _ from 'lodash';

/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import { lookupsState } from '../../redux/lookupsSlice';

import ButtonThemed from '../../components/ButtonTheme';
import { leadState, updateFinancialData } from '../../redux/leadSlice';
import {
  wizardState,
  setStepValidation,
  initUpdate,
  failUpdate,
  successUpdate,
  setStep,
  validateForm as validateFormAction,
} from '../../redux/wizardSlice';
/**
 * Components
 */
import formatters from '../../helpers/formatters';
import SelectTheme from '../../components/SelectTheme';
import Api from '../../helpers/api';
import VehicleName from '../../components/VehicleName';
import { getToken } from '../../helpers/auth';

const useStyles = makeStyles((theme) => ({
  navigationButtons: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    rowGap: 16,
    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-between',
      flexDirection: 'row',
      rowGap: 0,
      columnGap: 16,
      margin: '0 -16px',
    },
  },
  navigationButtonsCenter: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    rowGap: 16,
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      flexDirection: 'row',
      rowGap: 0,
      columnGap: 16,
      margin: '0 -16px',
    },
  },
  financialDetail: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    rowGap: 16,
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      flexDirection: 'row',
      rowGap: 0,
      columnGap: 16,
    },
  },
  box: {
    padding: 20,
    marginTop: 20,
    [theme.breakpoints.up('md')]: {
      marginLeft: 10,
    },
    minHeight: 200,
    backgroundColor: theme.palette.secondary.main,
  },
  price: {
    fontWeight: 'bold',
    fontSize: 28,
    color: theme.palette.primary.main,
  },
  colLeft: {
    textAlign: 'center',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50%',
      textAlign: 'right',
    },
  },
  colRight: {
    textAlign: 'center',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50%',
      textAlign: 'left',
    },
  },
  responsibility: {
    textAlign: 'justify',
    fontSize: 13,
    color: 'grey',
    fontStyle: 'italic',
    margin: '30px 0 129px 10px',
  },
}));

/**
 * @returns {JSX.Element}
 */
function PaymentSelect() {
  const f = (value) => formatters.thousand(value);
  const classes = useStyles();
  const { t } = useTranslation();
  const { configuration } = useSelector(lookupsState);
  const { step } = useSelector(wizardState);
  const {
    leadId,
    servicesTotal,
    financialData,
    personalData,
  } = useSelector(leadState);
  const { actionRequired } = useSelector(wizardState);
  const dispatch = useDispatch();

  const {
    max_period: maxPeriod,
    tan,
    insurance,
    insurance_calc: insuranceCalc,
    payments_collection_charges: paymentsCollectionCharges,
    handling_fee: handlingFee,
    max_price: maxAdvancePrice,
    min_price: minAdvancePrice,
  } = configuration.financial.conditions;
  const { financial: hasFinancial, trade_costs: tradeCosts } = configuration.financial;
  const { price: vehiclePrice } = configuration.vehicle;
  const { responsibility } = configuration.contact;
  /**
   * Load quote options list
   * @type {{text: string, value}[]}
   */
  const quoteList = Array(maxPeriod / 12)
    .fill()
    .map((__, index) => (
      {
        value: (index + 1) * 12,
        text: `${(index + 1) * 12} ${t('months')}`,
      }
    ));
  /**
   * Load advances options list
   * @type {{text: string, value}[]}
   */
  const maxAdvance = (vehiclePrice > maxAdvancePrice) || vehiclePrice === 0
    ? maxAdvancePrice : vehiclePrice;

  let advances = [];
  if (maxAdvance - minAdvancePrice > 0) {
    advances = Array(Math.round((maxAdvance - minAdvancePrice) / 100))
      .fill()
      .map((__, index) => (
        {
          value: (index) * 100 + Math.round(minAdvancePrice),
          text: `${f((index) * 100 + Math.round(minAdvancePrice))} €`,
        }
      ));
  }
  const getFinancialTypeDefaultValue = () => {
    if (financialData.type) {
      return financialData.type;
    }
    if (personalData.financing) {
      return 'financial';
    }
    return 'transfer';
  };
  /**
   * Formik instance
   */

  const formik = useFormik({
    initialValues: {
      type: getFinancialTypeDefaultValue(),
      advance: financialData.advance || _.get(advances, '[2].value', 0), // default advance
      fee: financialData.fee || 24, // default quoteList
      insurance,
      taeg: financialData.taeg || 0,
      quoteValue: financialData.quoteValue || 0,
    },
  });
  const financialAmount = () => {
    let realTradeCosts = 0;
    if (personalData.tradeIn) {
      realTradeCosts = formatters.toNumber(tradeCosts, 0);
    }
    return maxAdvance + servicesTotal + realTradeCosts - formik.values.advance;
  };
  /**
   * Call WP function to get financial data
   */
  const getFinancialData = () => {
    let res = {};
    const handlingFeeValue = formatters.toNumber(handlingFee, 0);
    if (window.GA && financialAmount() > 0) {
      /**
       * Retrieve financial calculated data
       * (ex. http://www.avvocatoandreani.it/servizi/calcolo-ammortamento-mutuo.php)
       *
       * @param amount {int}
       * @param quote_number {int}
       * @param insurance {number}
       * @param insurance_calc {string} Type of insurance value calculation
       * @param {number} payments_collection_charges
       * @param {number} tan
       * @param {number} handling_fee
       * @returns {{insurance: number, quote: *, taeg: number, quote_insurance: number}}
       */
      // If handlingFee is near to amount to financial getData goes in loop
      if (financialAmount() - handlingFeeValue > 299) {
        let insuranceValue = insurance;
        if (insuranceCalc === 'variable') {
          insuranceValue = insurance / 100;
        }
        const feeValue = formik.values.fee || 24;
        res = window.GA.Financial.getData(
          financialAmount(),
          feeValue,
          formatters.toNumber(insuranceValue, 0),
          insuranceCalc,
          formatters.toNumber(paymentsCollectionCharges, 0),
          tan,
          formatters.toNumber(handlingFee, 0),
        );
      }
    }
    formik.setFieldValue('taeg', res?.taeg);
    formik.setFieldValue('quoteValue', res?.quote);
  };
  /**
   * Submit form
   * @returns {Promise<void>}
   */
  const submitForm = async () => {
    const data = { id: leadId };
    if (formik.values.type === 'financial') {
      data.use_financial = true;
      data.financial_data = {
        tan,
        taeg: formik.values.taeg || 0,
        amount_financed: financialAmount(),
        prepayment_costs: formatters.toNumber(handlingFee, 0),
        period: formik.values.fee || 24,
        deposit: formik.values.advance || _.get(advances, '[2].value', 0),
      };
    } else {
      data.use_financial = false;
    }
    const res = await Api.patch({
      entryPoint: `/lead/${leadId}`,
      headers: { 'ga-token': getToken(leadId) },
      data,
    });

    if (res.status !== HttpStatus.OK) {
      dispatch(failUpdate());
      return;
    }
    dispatch(updateFinancialData(formik.values));
    dispatch(successUpdate());
  };
  /**
   * Effects
   */
  useEffect(() => {
    // No validation required
    dispatch(setStepValidation(true));
    dispatch(validateFormAction(false));
  }, []);
  useEffect(() => {
    if (actionRequired) {
      dispatch(initUpdate());
      submitForm();
    }
  }, [actionRequired]);
  useEffect(() => {
    getFinancialData();
  }, [window.GA, formik.values.fee, formik.values.advance]);
  /**
   * Auxiliar renders
   * @returns {JSX.Element}
   */
  const renderPrice = (value) => (
    <div className={`${classes.price} ga-ecommerce-payment-price`}>
      {`${formatters.thousand(value)} €`}
    </div>
  );
  useEffect(() => {
    if (configuration.vehicle.price <= 0) {
      dispatch(setStep(step + 1));
    }
  }, []);
  return (
    <div className="col-xs-12 ecommerce-payment ga-ecommerce-payment">
      { hasFinancial === 'true' && (
        <div className={`${classes.navigationButtons} buttons ga-ecommerce-payment-buttons`}>
          <ButtonThemed
            title={t('Financing')}
            selected={formik.values.type === 'financial'}
            onClick={() => formik.setFieldValue('type', 'financial')}
            className="ga-ecommerce-payment-button"
          />
          <ButtonThemed
            title={t('Bank transfer')}
            selected={formik.values.type === 'transfer'}
            onClick={() => formik.setFieldValue('type', 'transfer')}
            className="ga-ecommerce-payment-button"
          />
        </div>
      )}
      { hasFinancial !== 'true' && (
        <div className={classes.navigationButtonsCenter}>
          <ButtonThemed
            title={t('Bank transfer')}
            selected={formik.values.type === 'transfer'}
            onClick={() => formik.setFieldValue('type', 'transfer')}
            className="ga-ecommerce-payment-button"
          />
        </div>
      )}
      { formik.values.type === 'transfer' && (
        <>
          <div className={`${classes.box} resume ga-ecommerce-payment-resume-transfer`}>
            <VehicleName />
            <div className="ga-ecommerce-payment-resume-transfer-price" style={{ marginTop: 40 }}>
              { renderPrice(vehiclePrice + servicesTotal) }
            </div>
          </div>
          <div className={`${classes.responsibility} ga-ecommerce-payment-resume-transfer-responsibility`}>
            <p>{ responsibility }</p>
          </div>
        </>
      ) }
      { formik.values.type === 'financial' && (
        <>
          <div className={`${classes.box} resume ga-ecommerce-payment-resume-financial`}>
            <VehicleName />
            <div className={classes.financialDetail}>
              <div className={`ga-ecommerce-payment-financial-col left ${classes.colLeft}`}>
                { renderPrice(formik.values.quoteValue) }
                <div className="ga-ecommerce-payment-month">{t('per month')}</div>
              </div>
              <div className={`ga-ecommerce-payment-financial-col right ${classes.colRight}`}>
                <div>{`${t('Advance')} ${f(formik.values.advance)} €`}</div>
                <div>{`${t('Amount Financed')} ${f(financialAmount())} €`}</div>
                <div>{`${t('Prepayment costs')} ${f(handlingFee)} €`}</div>
                <div>{`${t('Duration')} ${f(formik.values.fee)} ${t('months')}`}</div>
                <div>{`${t('Insurance costs')} ${f(formik.values.insurance)} €`}</div>
                <div>{`${t('A.P.R.')} ${tan} %   ${t('E.A.P.R.')} ${parseFloat(formik.values.taeg).toFixed(1)} %`}</div>
              </div>
            </div>
          </div>
          <div className={`${classes.box} financial ga-ecommerce-payment-financial-data`}>
            <SelectTheme
              name="fee"
              formik={formik}
              title={t('Rate quantity')}
              items={quoteList}
            />
            <div>
              <SelectTheme
                name="advance"
                formik={formik}
                title={t('Advances')}
                items={advances}
                style={{ marginBottom: 0 }}
              />
            </div>
          </div>
        </>
      ) }
    </div>
  );
}

export default PaymentSelect;
